import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import {
  ApolloClient,
  ApolloProvider,
  NormalizedCacheObject,
} from '@apollo/client';
import isEqual from 'lodash/isEqual';

import { configApiRef, useAnalytics, useApi } from '@backstage/core-plugin-api';

import { createApolloClient } from '@spotify/confidence-graphql';

import { DialogProvider, useFeatureFlag } from '../../components';
import { Region } from '../../utils';
import { AuthFactoryProvider } from '../auth-factory';
import { CLIENT_ID } from '../auth-factory/auth0/auth';

export type ConfidenceCoreContextType = {
  config: Record<string, unknown>;
  setConfig: (v: Record<string, unknown>) => void;
  displayNames: Map<string, string>;
  setDisplayName: (v: Record<string, string>) => void;
  region?: Region;
  featureFlags: Record<string, boolean>;
  isLocalGraphql: boolean;
};

type ProviderConfig = {
  clientId?: string;
  redirectUri?: string;
  [key: string]: unknown;
};

const defaultProviderConfig: ProviderConfig = {
  clientId: CLIENT_ID,
};

const defaultValue: ConfidenceCoreContextType = {
  config: defaultProviderConfig,
  setConfig: () => {},
  displayNames: new Map(),
  setDisplayName: () => {},
  featureFlags: {},
  isLocalGraphql: false,
};

const ConfidenceCoreContext =
  createContext<ConfidenceCoreContextType>(defaultValue);

export const ConfidenceCoreProvider = ({
  config: { clientId = CLIENT_ID, redirectUri, ...c } = defaultProviderConfig,
  children,
  featureFlags = {},
}: PropsWithChildren<{
  config?: ProviderConfig;
  featureFlags?: Record<string, boolean>;
}>) => {
  const [token, setToken] = useState<string>();
  const [region, setRegion] = useState<Region>();
  const [config, setConfig] = useState(c);
  const analyticsApi = useAnalytics();

  const { enabled: requestAccounting } = useFeatureFlag(
    'graphql-request-timing',
    { enabled: false },
  );

  const appConfig = useApi(configApiRef);

  const [displayNames, setDisplayNames] = useState<
    ConfidenceCoreContextType['displayNames']
  >(defaultValue.displayNames);

  const handleTokenReceived = useCallback(
    (userToken: string, userRegion: Region) => {
      setToken(userToken);
      setRegion(userRegion);
    },
    [],
  );

  const uri = useMemo(
    () =>
      appConfig.getOptionalString('confidence.graphql') ??
      'https://graphql-konfidens.spotify.com/graphql',
    [appConfig],
  );

  const isLocalGraphql =
    process.env.NODE_ENV === 'development' && uri.includes('localhost');

  const apolloClient: ApolloClient<NormalizedCacheObject> = useMemo(() => {
    if (isLocalGraphql) {
      // eslint-disable-next-line no-console
      console.warn(`[Confidence] GraphQL pointing at local gateway: ${uri}`);
    }
    return createApolloClient({
      token,
      uri,
      region,
      analyticsApi: requestAccounting ? analyticsApi : undefined,
    });
  }, [token, region, analyticsApi, uri, isLocalGraphql, requestAccounting]);

  const updateDisplayName = useCallback(
    (v: Record<string, string>) => {
      setDisplayNames(current => {
        const newMap = new Map(current);
        Object.entries(v).forEach(([key, value]) => {
          newMap.set(key, value);
        });
        if (isEqual(current, newMap)) {
          return current;
        }
        return newMap;
      });
    },
    [setDisplayNames],
  );

  const value = useMemo<ConfidenceCoreContextType>(
    () => ({
      config,
      setConfig,
      displayNames,
      setDisplayName: updateDisplayName,
      region,
      featureFlags,
      isLocalGraphql,
    }),
    [
      config,
      displayNames,
      updateDisplayName,
      region,
      featureFlags,
      isLocalGraphql,
    ],
  );

  return (
    <ConfidenceCoreContext.Provider value={value}>
      <AuthFactoryProvider
        clientId={clientId}
        redirectUri={redirectUri}
        onTokenReceived={handleTokenReceived}
      >
        <ApolloProvider client={apolloClient}>
          <DialogProvider>{children}</DialogProvider>
        </ApolloProvider>
      </AuthFactoryProvider>
    </ConfidenceCoreContext.Provider>
  );
};

export const useConfidenceCoreContext = () => useContext(ConfidenceCoreContext);

export const useDisplayNames = () => {
  const { displayNames, setDisplayName } = useConfidenceCoreContext();

  return {
    displayNames,
    setDisplayName,
  };
};

export const useIsLocalGraphql = () => {
  const { isLocalGraphql } = useConfidenceCoreContext();
  return isLocalGraphql;
};

export const useRegion = () => {
  const { region } = useConfidenceCoreContext();
  return region;
};

export const useAppFeatureFlags = <
  T extends Record<string, boolean> = Record<string, boolean>,
>(): T => {
  const { featureFlags } = useConfidenceCoreContext();
  return featureFlags as T;
};
